import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import Wordmark from "@/components/Wordmark";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/auth/AuthContext";
import { api, apiError } from "@/lib/api";
import { setActiveSchemeId } from "@/components/AppShell";

export default function InviteAccept() {
  const { token } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get(`/invites/${token}/preview`);
        if (mounted) setPreview(data);
      } catch (e) {
        if (mounted) setError(apiError(e));
      }
    })();
    return () => {
      mounted = false;
    };
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      const { data } = await api.post(`/invites/${token}/accept`);
      setActiveSchemeId(data.scheme_id);
      toast.success(`Joined ${data.name}`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--slate-light)" }} data-testid="invite-accept-page">
      <header className="bg-white border-b" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-6xl mx-auto px-6 py-5">
          <Link to="/">
            <Wordmark size={26} />
          </Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md bg-white rounded-2xl border p-8 shadow-sm" style={{ borderColor: "var(--border)" }}>
          {error ? (
            <div data-testid="invite-error">
              <h1 className="text-2xl font-bold mb-2" style={{ color: "var(--navy)" }}>Invite not valid</h1>
              <p className="text-sm mb-5" style={{ color: "var(--slate)" }}>{error}</p>
              <Link to="/login">
                <Button variant="outline" className="w-full">Go to sign in</Button>
              </Link>
            </div>
          ) : preview ? (
            <div>
              <span className="inline-block text-[11px] font-semibold uppercase tracking-[0.18em] mb-3" style={{ color: "var(--blue-dark)" }}>
                You've been invited
              </span>
              <h1 className="text-2xl font-bold mb-1" style={{ color: "var(--navy)" }} data-testid="invite-scheme-name">
                Join {preview.scheme_name}
              </h1>
              <p className="text-sm mb-5" style={{ color: "var(--slate)" }}>
                Role: <strong style={{ color: "var(--navy)" }}>{preview.role}</strong> · {preview.scheme_state} scheme
              </p>

              {authLoading ? (
                <div className="flex gap-1.5 justify-center py-6">
                  <span className="ai-dot" /><span className="ai-dot" /><span className="ai-dot" />
                </div>
              ) : user ? (
                <div>
                  <p className="text-xs mb-4" style={{ color: "var(--tagline-grey)" }}>
                    Signed in as <strong>{user.email}</strong>. Accept to join the scheme.
                  </p>
                  <Button
                    onClick={accept}
                    disabled={accepting}
                    className="w-full h-11 font-semibold"
                    style={{ background: "var(--blue)", color: "white" }}
                    data-testid="invite-accept-btn"
                  >
                    {accepting ? "Joining…" : "Accept invite"}
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs mb-2" style={{ color: "var(--tagline-grey)" }}>
                    Invite is for <strong>{preview.invited_email}</strong>. Sign in or create an account to accept.
                  </p>
                  <Link
                    to="/login"
                    state={{ from: `/invite/${token}` }}
                    className="block"
                  >
                    <Button className="w-full h-11 font-semibold" style={{ background: "var(--blue)", color: "white" }} data-testid="invite-go-login">
                      Sign in to accept
                    </Button>
                  </Link>
                  <Link to="/signup" className="block">
                    <Button variant="outline" className="w-full h-11" data-testid="invite-go-signup">
                      Create an account
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <div className="flex gap-1.5 justify-center py-10">
              <span className="ai-dot" /><span className="ai-dot" /><span className="ai-dot" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
